import { useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import { toast } from 'sonner';
import { DEFAULT_TIMESHEET_SETTINGS } from '@/types/timesheetSettings';
import type { OrganisationTimesheetSettings, UtilisationSettings as UtilisationSettingsType } from '@/types/timesheetSettings';
import { runHealthCheck } from '@/services/settingsHealthCheck';
import { ApiError } from '@/services/apiClient';
import * as settingsRepository from '@/services/settingsRepository';
import { useSettingsValidation } from '@/hooks/useSettingsValidation';
import type { ValidationError } from '@/hooks/useSettingsValidation';
import { prefixKey } from '@/utils/storagePrefix';
import type { SettingsFormState } from './useSettingsFormState';
import type { SettingsData } from './useSettingsData';

export interface UseSettingsSaveDeps {
  form: SettingsFormState; // field state + setters (Session 2)
  data: SettingsData; // settings lifecycle state + setters (Session 3)
  setValidationErrors: Dispatch<SetStateAction<ValidationError[]>>; // page-owned
}

interface SaveContext {
  currentSettings: OrganisationTimesheetSettings | undefined;
  currentRowVersion: string | undefined;
}

interface RunSaveOptions {
  successMsg: string;
  successDescription: string;
  errorLog: string;
  errorMsg?: string;
  handleConflict?: boolean;
  errorDescription?: (err: unknown) => string;
  resetTabs?: () => void;
  onSaved?: (updated: OrganisationTimesheetSettings & { rowVersion?: string }) => void | Promise<void>;
}

/**
 * Every "save settings" flow for the SettingsPage. `runSave` factors out the
 * boilerplate shared by all five tab savers so each handler only builds its own
 * repository payload.
 */
export const useSettingsSave = ({ form, data, setValidationErrors }: UseSettingsSaveDeps) => {
  const { validateAllSettings } = useSettingsValidation();
  const [showCancelDialog, setShowCancelDialog] = useState(false);

  const {
    deadlineSettings, utilisationSettings, workingProfile, policyEnforcement, policyLimits, approvalScreen,
    weekStartDay, showRole, requireRole, showCostCentre, requireCostCentre, requireDescription, descriptionType,
    projectSelectionScope, showArchivedProjects, showProjectTimeRemaining,
    minimumEntryDuration, timeRounding, allowNegativeHours, historicalEntryLimit, allowFutureDatedEntries,
    publicHolidayCountryDefault, publicHolidayProject, publicHolidayCostCentre,
    enableCopyPreviousWeek, copyDescriptions, copyOnlyBillable, enableAutoSmartFill, enableAiInsights,
    overtimeSettings, hasUnsavedChanges, tabsWithChanges,
    setUtilisationSettings, setDeadlineSettings, setPolicyEnforcement, setPolicyLimits, setApprovalScreen,
    setHasUnsavedChanges, setTabsWithChanges,
  } = form;
  const {
    fullSettings, rowVersion, mode, isLive, ensureLiveSettings, refreshSettings, loadData,
    setFullSettingsPreservingWorkingProfile, setRowVersion, setIsSaving, setLastSaved,
    setHealthCheckResult, setConflictServerSettings, setShowConflictDialog,
  } = data;

  const runSave = async (
    build: (ctx: SaveContext) => Promise<OrganisationTimesheetSettings & { rowVersion?: string }>,
    opts: RunSaveOptions,
  ): Promise<boolean> => {
    setIsSaving(true);
    try {
      let currentSettings = fullSettings || undefined;
      let currentRowVersion = rowVersion;
      if (isLive) {
        const loaded = await ensureLiveSettings();
        if (!loaded) { setIsSaving(false); return false; }
        currentSettings = loaded.settings;
        currentRowVersion = loaded.version;
      }

      const updated = await build({ currentSettings, currentRowVersion });
      setFullSettingsPreservingWorkingProfile(updated);
      if ('rowVersion' in updated && updated.rowVersion) {
        setRowVersion(updated.rowVersion);
      }
      setHasUnsavedChanges(false);
      (opts.resetTabs ?? (() => setTabsWithChanges(new Set())))();
      setLastSaved({ timestamp: new Date(), user: 'You' });
      await opts.onSaved?.(updated);
      setHealthCheckResult(runHealthCheck(updated));
      toast.success(opts.successMsg, { description: opts.successDescription });
      return true;
    } catch (err) {
      console.error(opts.errorLog, err);

      if (opts.handleConflict !== false && err instanceof ApiError && err.status === 409) {
        try {
          const serverSettings = await settingsRepository.getOrganisationTimesheetSettings(mode);
          setConflictServerSettings(serverSettings);
          setShowConflictDialog(true);
        } catch {
          toast.error('Settings conflict', {
            description: 'Settings were modified by another user. Reloading...',
          });
          await loadData();
        }
      } else {
        toast.error(opts.errorMsg ?? 'Failed to save settings', {
          description: opts.errorDescription
            ? opts.errorDescription(err)
            : err instanceof ApiError ? err.message : 'Please try again',
        });
      }
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveUtilisation = async (settings: UtilisationSettingsType) => {
    await runSave(
      async ({ currentSettings, currentRowVersion }) => {
        const updated = await settingsRepository.updateUtilisationSettings(settings, currentSettings, currentRowVersion, mode);
        setUtilisationSettings(settings);
        return updated;
      },
      { successMsg: 'Settings saved successfully', successDescription: 'Your changes have been applied', errorLog: 'Error saving utilisation settings:' },
    );
  };

  const handleSaveWorkingHoursSettings = async (): Promise<boolean> => {
    if (!workingProfile || !policyEnforcement) return false;

    if (publicHolidayCountryDefault && !publicHolidayProject) {
      toast.error('Project is required', {
        description: 'Please select a project for public holiday time entry allocation when a country is selected.',
      });
      return false;
    }
    if (publicHolidayCountryDefault && !publicHolidayCostCentre) {
      toast.error('Cost Centre is required', {
        description: 'Please select a cost centre for public holiday time entry allocation when a country is selected.',
      });
      return false;
    }

    return runSave(
      async ({ currentSettings, currentRowVersion }) => {
        // Only save policy enforcement settings, not working hours. Working
        // hours are managed in Resource Manager and remain read-only.
        const updated = await settingsRepository.updateWorkingHoursSettings(
          workingProfile, policyEnforcement, policyLimits, approvalScreen,
          currentSettings, currentRowVersion, mode,
          publicHolidayCountryDefault, publicHolidayProject, publicHolidayCostCentre,
        );
        // Don't update workingProfile state as it should remain unchanged
        setPolicyEnforcement(policyEnforcement);
        setPolicyLimits(policyLimits);
        setApprovalScreen(approvalScreen);
        return updated;
      },
      { successMsg: 'Policy settings saved successfully', successDescription: 'Your policy check changes have been applied', errorLog: 'Error saving policy settings:' },
    );
  };

  const handleBeforeSync = async () => {
    const saved = await handleSaveWorkingHoursSettings();
    if (!saved) {
      throw new Error('Save failed');
    }
  };

  // Save handler for Submissions & Deadlines tab (deadline settings only)
  const handleSaveGeneralSettings = async () => {
    if (!deadlineSettings) return;

    await runSave(
      async ({ currentSettings, currentRowVersion }) =>
        settingsRepository.updateDeadlineSettings(deadlineSettings, currentSettings, currentRowVersion, mode),
      { successMsg: 'Settings saved successfully', successDescription: 'Your changes have been applied', errorLog: 'Error saving deadline settings:' },
    );
  };

  // Save handler for Time Entry Rules tab (data requirements only)
  const handleSaveDataRequirementsSettings = async () => {
    // Validate all settings before saving
    const validation = validateAllSettings(
      deadlineSettings, utilisationSettings, policyLimits, historicalEntryLimit, minimumEntryDuration,
      showRole, requireRole, showCostCentre, requireCostCentre,
    );

    if (!validation.isValid) {
      setValidationErrors(validation.errors);
      toast.error('Validation failed', {
        description: `Please fix ${validation.errors.length} error${validation.errors.length > 1 ? 's' : ''} before saving`,
      });
      return;
    }

    // Clear validation errors
    setValidationErrors([]);

    const updatedDeadlineSettings = {
      ...deadlineSettings!,
      enableCopyPreviousWeek, copyDescriptions, copyOnlyBillable, enableAutoSmartFill, enableAiInsights,
    };

    await runSave(
      async ({ currentSettings, currentRowVersion }) => {
        const mergedSettings: OrganisationTimesheetSettings = {
          ...(currentSettings || {} as OrganisationTimesheetSettings),
          weekStartDay, showRole, requireRole, showCostCentre, requireCostCentre,
          requireDescription, descriptionType, projectSelectionScope,
          showArchivedProjects, showProjectTimeRemaining, minimumEntryDuration, timeRounding,
          allowNegativeHours, historicalEntryLimit, allowFutureDatedEntries,
          policyViolations: policyEnforcement ?? currentSettings?.policyViolations ?? DEFAULT_TIMESHEET_SETTINGS.policyViolations,
          deadline: updatedDeadlineSettings,
        };

        return settingsRepository.updateOrganisationTimesheetSettings(mergedSettings, currentRowVersion, mode);
      },
      {
        successMsg: 'Settings saved successfully',
        successDescription: 'Your changes have been applied',
        errorLog: 'Error saving data requirements:',
        onSaved: async () => {
          setDeadlineSettings(updatedDeadlineSettings);
          setValidationErrors([]);
          // Save smart features to localStorage for role permissions access
          localStorage.setItem(prefixKey('globalTimesheetSettings'), JSON.stringify({
            enableCopyPreviousWeek, copyDescriptions, copyOnlyBillable, enableAutoSmartFill, enableAiInsights,
          }));
          // Refresh global settings context to ensure all components are updated
          await refreshSettings();
        },
      },
    );
  };

  const handleSaveOvertimeSettings = async () => {
    await runSave(
      async ({ currentSettings, currentRowVersion }) => {
        const mergedSettings: OrganisationTimesheetSettings = {
          ...(currentSettings || DEFAULT_TIMESHEET_SETTINGS),
          overtime: overtimeSettings,
        };
        return settingsRepository.updateOrganisationTimesheetSettings(mergedSettings, currentRowVersion, mode);
      },
      {
        successMsg: 'Work type settings saved',
        successDescription: 'Your work type configuration has been updated.',
        errorLog: 'Error saving work type settings:',
        errorMsg: 'Failed to save work type settings',
        handleConflict: false,
        errorDescription: (err) => (err instanceof Error ? err.message : 'Please try again'),
        resetTabs: () => setTabsWithChanges(prev => {
          const next = new Set(prev);
          next.delete('overtime');
          return next;
        }),
        onSaved: async () => { await refreshSettings(); },
      },
    );
  };

  const handleSave = async () => {
    if (tabsWithChanges.has('general')) await handleSaveDataRequirementsSettings();
    if (tabsWithChanges.has('working-hours')) await handleSaveWorkingHoursSettings();
    if (tabsWithChanges.has('submissions-deadlines')) await handleSaveGeneralSettings();
    if (tabsWithChanges.has('utilisation') && utilisationSettings) await handleSaveUtilisation(utilisationSettings);
    if (tabsWithChanges.has('overtime')) await handleSaveOvertimeSettings();
  };

  const handleCancel = () => {
    if (hasUnsavedChanges) {
      setShowCancelDialog(true);
    }
  };

  const handleConfirmCancel = async () => {
    setShowCancelDialog(false);
    await loadData();
    setHasUnsavedChanges(false);
    setTabsWithChanges(new Set());
    setValidationErrors([]);
  };

  return {
    showCancelDialog,
    setShowCancelDialog,
    handleSaveUtilisation,
    handleSaveWorkingHoursSettings,
    handleSaveGeneralSettings,
    handleSaveDataRequirementsSettings,
    handleSaveOvertimeSettings,
    handleBeforeSync,
    handleSave,
    handleCancel,
    handleConfirmCancel,
  };
};

export type SettingsSave = ReturnType<typeof useSettingsSave>;
